<template>
  <div class="dashboard-container">
    <v-container fluid class="pa-4">
      <!-- Dashboard Header -->
      <v-row>
        <v-col cols="12">
          <h1 class="dashboard-title mb-4">Admin Dashboard</h1>
        </v-col>
      </v-row>
      
      <!-- Top Row: PPH Monitor and Online Users -->
      <v-row>
        <v-col cols="12">
          <PPHMonitor 
            :stats="pphStats"
            :history="pphHistory"
          />
        </v-col>
       
      </v-row>

      <!-- Second Row: Packlist Activity and Admin Events -->
      <v-row class="mt-4">
        <v-col cols="12" lg="7">
          <PacklistActivity
            :active-packers="activePackers"
            :recent-packed-orders="recentPackedOrders"
            :stats="packlistStats"
            :cache-info="cacheInfo"
            :loading-more-events="loadingMoreEvents"
            @load-more="loadMorePacklistEvents"
          />
          <OnlineUsers
            :users="onlineUsers"
            :stats="userStats"
          />
        </v-col>
        <v-col cols="12" lg="5">
          <OrdersCard
            v-if="orders"
            :orders="orders"
          />
          <AdminEvents
            :events="adminEvents"
            :broadcasting="broadcasting"
            @send-notification="sendSystemNotification"
            @refresh-users="forceRefreshUsers"
            @broadcast="broadcastMessage"
            @clear-events="clearEvents"
          />

        
        </v-col>
      </v-row>
      
      <!-- Third Row: Orders, Printers, MongoDB -->
     
      
      <!-- Fourth Row: System Information and Processes -->
      <v-row class="mt-4">
        <v-col cols="12" lg="6">
          <SystemInfo
            v-if="system"
            :system="system"
            @refresh="refresh"
          />
        </v-col>
        <v-col cols="12" lg="6">
          <v-card v-if="processes && processes.length" class="process-card" elevation="4">
            <v-card-title>
              <h2 class="section-title">Laufende Prozesse</h2>
              <v-spacer></v-spacer>
              <v-chip color="secondary" small>{{ processList.length }} Prozesse</v-chip>
            </v-card-title>
            <v-card-text class="pa-0">
              <v-data-table
                :headers="processHeaders"
                :items="processList"
                :items-per-page="15"
                :footer-props="{ 'items-per-page-options': [10, 15, 25, 50, -1] }"
                class="process-table"
                dark
                dense
                sort-by="memory"
                :sort-desc="true"
              >
                <template v-slot:item.status="{ item }">
                  <v-chip
                    small
                    :color="item.status === 'online' ? 'success' : 'error'"
                    class="status-chip"
                  >
                    {{ item.status }}
                  </v-chip>
                </template>
                <template v-slot:item.memory="{ item }">
                  <div>
                    <v-progress-linear
                      :value="Math.min(item.memory / 10, 100)"
                      :color="getMemoryColor(item.memory)"
                      height="8"
                      rounded
                    ></v-progress-linear>
                    <small>{{ item.memory.toFixed(0) }} MB</small>
                  </div>
                </template>
                <template v-slot:item.restarts="{ item }">
                  <v-chip 
                    x-small 
                    :color="item.restarts > 100 ? 'warning' : 'primary'"
                    dark
                  >
                    {{ item.restarts }}
                  </v-chip>
                </template>
                <template v-slot:item.version="{ item }">
                  <span class="caption">{{ item.version }}</span>
                </template>
                <template v-slot:item.actions>
                  <v-btn icon x-small text>
                    <v-icon small>mdi-restart</v-icon>
                  </v-btn>
                </template>
              </v-data-table>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <v-row class="mt-4">
      
      <v-col cols="12" md="4">
        <PrintersCard
          v-if="printers && printers.length"
          :printers="printers"
          :print-history="printHistory"
        />
      </v-col>
      <v-col cols="12" md="8">
        <MongoDBCard
          v-if="mongoDB"
          :mongodb="mongoDB"
          :collections="mongoCollections"
          :recent-operations="mongoOperations"
          :performance-data="mongoPerformance"
        />
      </v-col>
    </v-row>
    </v-container>
  </div>
</template>

<script>
import Header from "./header";
import dashboardService from "@/services/DashboardService";
import io from 'socket.io-client';

// Dashboard Components
import PPHMonitor from '@/components/Dashboard/PPHMonitor.vue';
import PacklistActivity from '@/components/Dashboard/PacklistActivity.vue';
import OnlineUsers from '@/components/Dashboard/OnlineUsers.vue';
import AdminEvents from '@/components/Dashboard/AdminEvents.vue';
import SystemInfo from '@/components/Dashboard/SystemInfo.vue';
import OrdersCard from '@/components/Dashboard/OrdersCard.vue';
import PrintersCard from '@/components/Dashboard/PrintersCard.vue';
import MongoDBCard from '@/components/Dashboard/MongoDBCard.vue';

export default {
  name: "dashboard",
  
  components: {
    Header,
    PPHMonitor,
    PacklistActivity,
    OnlineUsers,
    AdminEvents,
    SystemInfo,
    OrdersCard,
    PrintersCard,
    MongoDBCard,
  },
  
  data: () => ({
    // Loading states
    loading: true,
    broadcasting: false,
    loadingMoreEvents: false,
    
    // Socket connections
    messagesSocket: null,
    adminSocket: null,
    
    // System data
    system: null,
    processes: [],
    printers: [],
    orders: null,
    mongoDB: null,
    
    // Additional data for components
    printHistory: [],
    mongoCollections: [],
    mongoOperations: [],
    mongoPerformance: [],
    
    // Online Users
    onlineUsers: [],
    userStats: {
      totalOnline: 0,
      activeUsers: 0,
      adminCount: 0,
    },
    
    // Admin Events
    adminEvents: [],
    
    // Packlist data
    activePackers: [],
    recentPackedOrders: [],
    packlistStats: {
      todayTotal: 0,
      avgTime: '00:00',
      activeNow: 0
    },
    
    // Cache info
    cacheInfo: {
      loadedPacklistEvents: 0,
      totalPacklistEvents: 0,
      canLoadMore: false
    },
    
    // PPH Statistics
    pphStats: {
      current: 0,
      average: 0,
      high: 0,
      low: 0
    },
    pphHistory: [],
    
    // Timers
    timer: null,
    packingTimers: {},
  }),
  
  computed: {
    processList() {
      if (!this.processes || !Array.isArray(this.processes)) return [];
      
      return this.processes.map(p => ({
        name: p.name,
        status: p.status || 'unknown',
        memory: p.memory || 0,  // Already in MB from backend
        cpu: p.cpu || 0,
        restarts: p.restarts || 0,
        version: p.version || 'N/A',
        uptime: this.formatUptime(p.created)
      }));
    },
    
    processHeaders() {
      return [
        { text: 'Name', value: 'name', width: '25%' },
        { text: 'Status', value: 'status', width: '10%' },
        { text: 'Memory (MB)', value: 'memory', width: '15%' },
        { text: 'Restarts', value: 'restarts', width: '10%' },
        { text: 'Version', value: 'version', width: '15%' },
        { text: 'Uptime', value: 'uptime', width: '15%' },
        { text: 'Actions', value: 'actions', sortable: false, width: '10%' }
      ];
    }
  },
  
  async mounted() {
    await this.refresh();
    this.setupSocketConnections();
    this.timer = setInterval(this.refresh, 60000);
    
    // Always initialize with mock data if no PPH data exists
    // Real data will override when received from socket
    if (this.pphHistory.length === 0) {
      console.log('[AdminDashboard] Initializing with mock PPH data');
      this.initializeMockPPHData();
    }
  },
  
  beforeDestroy() {
    if (this.timer) clearInterval(this.timer);
    this.cleanupSockets();
  },
  
  methods: {
    async refresh() {
      this.loading = true;
      try {
        const data = (await dashboardService.getAdminDashbaord()).data;
        
        this.system = data.system;
        this.processes = data.processes;
        
        // Use real data from backend
        this.printers = data.printers;
        this.orders = data.orders;
        this.mongoDB = data.mongo; // Note: backend sends 'mongo' not 'mongoDB'
        
        // Populate additional component data
        this.printHistory = data.printHistory || [];
        
        // Use collections from MongoDB status if available
        if (this.mongoDB && this.mongoDB.collections) {
          this.mongoCollections = this.mongoDB.collections;
        } else {
          this.mongoCollections = this.generateMockCollections();
        }
        
        // Use performance data from MongoDB status if available
        if (this.mongoDB && this.mongoDB.recentOperations) {
          this.mongoOperations = this.mongoDB.recentOperations;
        } else {
          this.mongoOperations = [];
        }
        
        if (this.mongoDB && this.mongoDB.performance && this.mongoDB.performance.history) {
          this.mongoPerformance = this.mongoDB.performance.history;
        } else {
          this.mongoPerformance = [];
        }
        
        this.loading = false;
      } catch (error) {
        console.error('Dashboard refresh error:', error);
        this.loading = false;
      }
    },
    
    setupSocketConnections() {
      // Setup messages socket
      this.messagesSocket = io('/messages', {
        auth: {
          userId: localStorage.getItem('userId') || 'anonymous',
          username: localStorage.getItem('username') || 'Unknown',
          role: localStorage.getItem('userRole') || 'user'
        }
      });
      
      // Setup admin socket
      this.adminSocket = io('/admin-events', {
        auth: {
          userId: localStorage.getItem('userId') || 'admin',
          role: 'admin'
        }
      });
      
      this.setupSocketListeners();
      
      // Request initial data after connection
      setTimeout(() => {
        this.adminSocket.emit('request-pph-stats');
        this.adminSocket.emit('request-online-users');
      }, 100);
    },
    
    setupSocketListeners() {
      // Online users events
      this.adminSocket.on('online-users', (users) => {
        this.onlineUsers = users;
        this.updateUserStats(users);
      });
      
      // Admin events
      this.adminSocket.on('admin-event', (event) => {
        this.addAdminEvent(event);
      });
      
      // Packlist events
      this.adminSocket.on('packlist-event', (event) => {
        this.handlePacklistEvent(event);
      });
      
      // PPH events
      this.adminSocket.on('pph-update', (data) => {
        console.log('[AdminDashboard] PPH Update received:', data);
        
        // Calculate stats from history if not provided
        let average = 0, high = 0, low = 999999;
        if (data.history && data.history.length > 0) {
          const values = data.history.map(h => h.y || 0);
          average = Math.round(values.reduce((a, b) => a + b, 0) / values.length);
          high = Math.max(...values);
          low = Math.min(...values);
        }
        
        this.pphStats = {
          current: data.current || 0,
          average: data.average || average,
          high: data.high || data.peak || high,
          low: data.low !== undefined ? data.low : low
        };
        
        this.pphHistory = data.history || [];
        console.log('[AdminDashboard] PPH Stats updated:', this.pphStats);
      });
      
      this.adminSocket.on('pph-stats', (stats) => {
        console.log('[AdminDashboard] PPH Stats received:', stats);
        
        // Calculate stats from history if not provided
        let average = 0, high = 0, low = 999999;
        if (stats.history && stats.history.length > 0) {
          const values = stats.history.map(h => h.y || 0);
          average = Math.round(values.reduce((a, b) => a + b, 0) / values.length);
          high = Math.max(...values);
          low = Math.min(...values);
        }
        
        this.pphStats = {
          current: stats.current || 0,
          average: stats.average || average,
          high: stats.peak || stats.high || high,
          low: stats.low !== undefined ? stats.low : low
        };
        
        if (stats.history) {
          this.pphHistory = stats.history;
        }
        console.log('[AdminDashboard] PPH Stats updated:', this.pphStats);
      });
      
      // Cached events
      this.adminSocket.on('cached-packlist-events', (events) => {
        this.activePackers = [];
        events.reverse().forEach(event => {
          this.handlePacklistEvent(event, false);
        });
      });
      
      this.adminSocket.on('cache-info', (info) => {
        this.cacheInfo = info;
      });
    },
    
    updateUserStats(users) {
      this.userStats = {
        totalOnline: users.length,
        activeUsers: users.filter(u => u.isActive).length,
        adminCount: users.filter(u => u.role === 'admin').length
      };
    },
    
    addAdminEvent(event) {
      const exists = this.adminEvents.some(e => 
        e.type === event.type && 
        e.userId === event.userId && 
        Math.abs(new Date(e.timestamp) - new Date(event.timestamp)) < 1000
      );
      
      if (!exists) {
        this.adminEvents.unshift(event);
        if (this.adminEvents.length > 100) {
          this.adminEvents.pop();
        }
      }
    },
    
    handlePacklistEvent(event, animate = true) {
      if (event.type === 'packlist-track-start') {
        this.handlePacklistStart(event);
      } else if (event.type === 'packlist-track-end') {
        this.handlePacklistEnd(event, animate);
      } else if (event.type === 'packlist-multicart-slot') {
        this.handleMultiCartSlot(event, animate);
      }
      
      this.addAdminEvent(event);
      this.updatePacklistStats();
    },
    
    handlePacklistStart(event) {
      let packer = this.activePackers.find(p => p.userId === event.userId);
      
      if (!packer) {
        packer = {
          userId: event.userId,
          username: event.username || event.userId,
          isPacking: false,
          currentOrder: null,
          startTime: null
        };
        this.activePackers.push(packer);
      }
      
      // Handle both old and new event structure
      const data = event.data || event;
      packer.isPacking = true;
      packer.currentOrder = data.orderNumber || data.orderId || event.orderNumber || event.orderId || 'N/A';
      packer.startTime = data.startTime || event.startTime || event.timestamp;
      packer.isStale = false;
    },
    
    handlePacklistEnd(event, animate = true) {
      const packer = this.activePackers.find(p => p.userId === event.userId);
      if (packer) {
        packer.isPacking = false;
        packer.currentOrder = null;
        
        const packingTime = packer.startTime ? 
          new Date(event.timestamp) - new Date(packer.startTime) : 0;
        
        packer.startTime = null;
        
        // Handle both old and new event structure
        const data = event.data || event;
        
        this.recentPackedOrders.unshift({
          id: `${event.userId}-${event.timestamp}`,
          username: event.username || event.userId,
          orderNumber: data.orderNumber || data.orderId || event.orderNumber || event.orderId || 'N/A',
          orderId: data.orderId || event.orderId,  // Keep orderId for the component
          timestamp: event.timestamp,
          packingTime: packingTime,
          isNew: animate,
          isMultiCart: false
        });
        
        if (this.recentPackedOrders.length > 100) {
          this.recentPackedOrders.pop();
        }
        
        if (animate) {
          setTimeout(() => {
            const item = this.recentPackedOrders.find(p => 
              p.id === `${event.userId}-${event.timestamp}`
            );
            if (item) item.isNew = false;
          }, 1000);
        }
      }
    },
    
    handleMultiCartSlot(event, animate = true) {
      // Handle both old and new event structure
      const data = event.data || event;
      
      this.recentPackedOrders.unshift({
        id: `mc-${event.userId}-${event.timestamp}`,
        username: event.username || event.userId,
        orderNumber: data.orderNumber || data.orderId || event.orderNumber || event.orderId || 'N/A',
        orderId: data.orderId || event.orderId,  // Keep orderId for the component
        timestamp: event.timestamp,
        isMultiCart: true,
        itemCount: data.itemCount || event.itemCount || 1,
        location: data.location || event.location || 'Unbekannt',
        isNew: animate
      });
      
      if (this.recentPackedOrders.length > 100) {
        this.recentPackedOrders.pop();
      }
      
      if (animate) {
        setTimeout(() => {
          const item = this.recentPackedOrders.find(p => 
            p.id === `mc-${event.userId}-${event.timestamp}`
          );
          if (item) item.isNew = false;
        }, 1000);
      }
    },
    
    updatePacklistStats() {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      
      const todayPacked = this.recentPackedOrders.filter(pack => {
        const packDate = new Date(pack.timestamp);
        return packDate >= today && !pack.isMultiCart;
      });
      
      this.packlistStats = {
        todayTotal: todayPacked.length,
        avgTime: this.calculateAvgPackingTime(todayPacked),
        activeNow: this.activePackers.filter(p => p.isPacking).length
      };
    },
    
    calculateAvgPackingTime(packs) {
      const packsWithTime = packs.filter(p => p.packingTime > 0);
      if (packsWithTime.length === 0) return '00:00';
      
      const avgMs = packsWithTime.reduce((sum, p) => sum + p.packingTime, 0) / packsWithTime.length;
      const minutes = Math.floor(avgMs / 60000);
      const seconds = Math.floor((avgMs % 60000) / 1000);
      
      return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
    },
    
    async loadMorePacklistEvents() {
      this.loadingMoreEvents = true;
      this.adminSocket.emit('load-more-packlist-events', {
        offset: this.cacheInfo.loadedPacklistEvents,
        limit: 500
      });
      
      this.adminSocket.once('more-packlist-events', (data) => {
        data.events.reverse().forEach(event => {
          this.handlePacklistEvent(event, false);
        });
        
        this.cacheInfo = data.cacheInfo;
        this.loadingMoreEvents = false;
      });
    },
    
    sendSystemNotification() {
      // Implement system notification
      console.log('Send system notification');
    },
    
    forceRefreshUsers() {
      this.adminSocket.emit('request-online-users');
    },
    
    broadcastMessage() {
      this.broadcasting = true;
      // Implement broadcast message
      setTimeout(() => {
        this.broadcasting = false;
      }, 2000);
    },
    
    clearEvents() {
      this.adminEvents = [];
    },
    
    cleanupSockets() {
      if (this.messagesSocket) {
        this.messagesSocket.disconnect();
      }
      if (this.adminSocket) {
        this.adminSocket.disconnect();
      }
    },
    
    formatUptime(timestamp) {
      if (!timestamp) return 'N/A';
      const diff = Date.now() - timestamp;
      const days = Math.floor(diff / (1000 * 60 * 60 * 24));
      const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      return `${days}d ${hours}h`;
    },
    
    getMemoryColor(memory) {
      if (memory < 100) return 'success';
      if (memory < 500) return 'warning';
      return 'error';
    },
    
    // Generate mock collections if no real data
    generateMockCollections() {
      return [
        { name: 'orders', documents: 0, size: 0, indexes: 0, avgObjSize: 0 },
        { name: 'packlistV2', documents: 0, size: 0, indexes: 0, avgObjSize: 0 }
      ];
    },
    
    initializeMockPPHData() {
      // Generate mock PPH data for the last 24 hours
      const now = new Date();
      const history = [];
      
      for (let i = 24; i >= 0; i--) {
        const timestamp = new Date(now - i * 3600000);
        const baseValue = 35 + Math.sin(i / 4) * 15;
        const value = Math.max(0, Math.floor(baseValue + Math.random() * 10));
        
        history.push({
          x: timestamp.toISOString(),
          y: value,
          timestamp: timestamp.toISOString(),
          value: value
        });
      }
      
      this.pphHistory = history;
      
      // Calculate stats from history
      const values = history.map(h => h.y);
      this.pphStats = {
        current: values[values.length - 1],
        average: Math.round(values.reduce((a, b) => a + b, 0) / values.length),
        high: Math.max(...values),
        low: Math.min(...values)
      };
      
      console.log('[AdminDashboard] Initialized mock PPH data:', this.pphStats);
    }
  }
};
</script>

<style scoped>
.dashboard-container {
  background: linear-gradient(135deg, #1e1e1e 0%, #2d2d2d 100%);
  min-height: 100vh;
}

.dashboard-title {
  font-size: 2rem;
  font-weight: 300;
  color: #fff;
  text-transform: uppercase;
  letter-spacing: 2px;
}

.section-title {
  font-size: 1.2rem;
  font-weight: 500;
  color: #fff !important;
}

.process-card {
  background: linear-gradient(135deg, rgba(30, 30, 30, 0.95) 0%, rgba(20, 20, 20, 0.95) 100%);
}

.process-card ::v-deep .v-data-table {
  background: transparent !important;
}

.process-card ::v-deep .v-data-table th {
  color: #fff !important;
}

.process-card ::v-deep .v-data-table td {
  color: rgba(255, 255, 255, 0.9) !important;
}

.orders-card,
.printers-card,
.mongo-card {
  background: linear-gradient(135deg, rgba(30, 30, 30, 0.95) 0%, rgba(20, 20, 20, 0.95) 100%);
}

/* Global text color fixes */
::v-deep .v-card__title {
  color: #fff !important;
}

::v-deep .v-card__text {
  color: rgba(255, 255, 255, 0.9) !important;
}

::v-deep .v-list-item__title {
  color: #fff !important;
}

::v-deep .v-list-item__subtitle {
  color: rgba(255, 255, 255, 0.7) !important;
}
</style>